import { clientSendVoiceError } from 'constants/errors';
import { clientAgentId, sharingMessages, techAgentId } from 'constants/history';
import { AgentType } from 'enums/AgentType';
import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { InputWidgetElementType } from 'enums/InputWidgetElementType';
import { MessageType } from 'enums/MessageType';
import { StorageKey } from 'enums/StorageKey';
import { WsAction } from 'enums/WsAction';
import { AgentData } from 'interfaces/AgentData';
import { BaseWsMessage } from 'interfaces/BaseWsMessage';
import { MessageData } from 'interfaces/MessageData';
import { ReactionAddedMessage, ReactionRemovedMessage } from 'interfaces/UnreadReactions';
import { WsAsrResultReplyMessage } from 'interfaces/WsAsrResultMessage';
import { DefaultRootState } from 'react-redux';
import { Dispatch } from 'redux';
import { IncomingActionService } from 'services/IncomingActionService';
import { receiveAgent, receiveAgents } from 'state/agents/agents.actions';
import { receiveAgentTyping, setUnreadCounter } from 'state/chat/chat.actions';
import { commitLastAsrResult, rollbackLastAsrResult } from 'state/clientVoice/clientVoice.actions';
import { receiveCommand } from 'state/commands/commands.actions';
import { csiReset, setCsiState } from 'state/csi/csi.actions';
import { setAllowedFileExtensions } from 'state/files/files.actions';
import {
    addClientSideMessage,
    clearLastAgent,
    markMessageRead,
    markMessageReceived,
    receiveHistory,
    receiveMessage,
    requestUnreadReactions,
    setAppealClosedBy,
    setLastAgent,
    setMessageReactions,
    setMessagesReceivedOnlineIdList,
    setOperatorAvailable,
    setOperatorAwaitTimeMs,
    setUnreadReactions,
} from 'state/history/history.actions';
import { receiveInputWidget } from 'state/inputWidget/inputWidget.actions';
import {
    receiveSharingMessage,
    startSharingViewer,
    stopSharingViewer,
} from 'state/sharing/sharing.actions';
import { _counterLog } from 'utils/_counterLog';
import { wsToChatAgent } from 'utils/agents';
import { createReachGoalActionBy } from 'utils/analytics';
import { secondsToMilliseconds } from 'utils/dateTime';
import { ChatPerfomance, EDtrumPerformanceEvent } from 'utils/dtrum';
import { filterMessageByAgentId, getMaxAwaitTimeSec, wsToChatMessage } from 'utils/messages';
import { isMessageCommand, isMessageReadable } from 'utils/messages';
import { randomStr, randomUuid } from 'utils/random';

import { services } from '../container';
import { ServiceConfiguration } from '../ServiceConfiguration';

let isFirstHistoryReply = true;
let startTime = 0;
let expectedTime = 0;
let timerFlag;

const incomingActionHandlers: Partial<
    Record<
        WsAction,
        (
            action: BaseWsMessage,
            dispatch: Dispatch,
            token: string,
            getState?: () => DefaultRootState,
        ) => void
    >
> = {
    [WsAction.State]: (action, dispatch) => {
        dispatch(setOperatorAvailable(!!action.data.available_operator, !!action.data.chat_now));
    },

    // получение истории
    [WsAction.HistoryReply]: (action, dispatch, token, getState) => {
        dispatch(requestUnreadReactions());
        const { data } = action;

        const {
            available_operator,
            chat_now,
            last_read_message_id,
            last_received_message_id,
            type,
            requestedMsgId,
            active_sharing_room,
        } = data;

        const client = data.client || null;

        const wsMessages = filterMessageByAgentId(data.messages, -1) || {};
        const messages: { [msgId: string]: MessageData } = {};
        let lastMessageFromAgent = {
            agentId: '1',
            agentType: AgentType.Bot,
        };

        const existingMessages = getState().history.messages;
        for (const msgIdStr in wsMessages) {
            let msg: MessageData = existingMessages[msgIdStr];

            if (!msg) {
                msg = wsToChatMessage(wsMessages[msgIdStr]);
                msg.uuid ??= randomUuid();
            }

            if (+msg?.agentId > 0 && isFirstHistoryReply) {
                const { agentId, agentType } = msg;
                lastMessageFromAgent = { agentId, agentType };
            }

            if (isMessageReadable(msg)) {
                messages[msg.id] = msg;
            }
        }

        const wsAgents = data.agents || {};
        const agents: { [agentId: string]: AgentData } = {};

        for (const agentIdStr in wsAgents) {
            const agent = wsToChatAgent(wsAgents[agentIdStr]);

            if (agent.id !== techAgentId) {
                agents[agent.id] = agent;
            }
        }

        const allowedExtensions: string[] = data.allowed_ext;
        if (allowedExtensions && allowedExtensions.length > 0) {
            dispatch(setAllowedFileExtensions(allowedExtensions));
        }

        dispatch(receiveAgents(agents));

        dispatch(
            receiveHistory({
                operatorAvailable: !!available_operator,
                chatNow: !!chat_now,
                messages,
                lastReadMessageId: last_read_message_id,
                lastReceivedMessageId: last_received_message_id,
                type,
                requestedMsgId,
                client,
            }),
        );

        if (isFirstHistoryReply) {
            const { agentId: id, agentType: type } = lastMessageFromAgent;
            dispatch(setAppealClosedBy({ id, type }));
            isFirstHistoryReply = false;
            ChatPerfomance.addProperties(EDtrumPerformanceEvent.READY);
            ChatPerfomance.endAction();
        }

        if (!type && active_sharing_room) {
            dispatch(startSharingViewer(token, active_sharing_room));
        }
    },

    // назначение агента, у нас есть открытое обращение
    [WsAction.ChangeAgent]: (action, dispatch, _token, getState) => {
        interface ChangeAgentData {
            id: string;
            name: string;
            photo: string;
            type: AgentType;
        }

        const state = getState();
        const agent: ChangeAgentData = action.agent;
        const maxAwaitTimeSec = getMaxAwaitTimeSec(state, action);

        clearTimeout(timerFlag);
        const performAwaitTimeActions = (maxAwaitTimeSec: number) => {
            dispatch(setOperatorAwaitTimeMs(secondsToMilliseconds(maxAwaitTimeSec)));
            startTime = Date.now();
            expectedTime = maxAwaitTimeSec;
        };

        if ((maxAwaitTimeSec || maxAwaitTimeSec === 0) && !agent) {
            if (maxAwaitTimeSec === 0) {
                timerFlag = setTimeout(() => {
                    performAwaitTimeActions(maxAwaitTimeSec);
                }, 500);
            } else {
                performAwaitTimeActions(maxAwaitTimeSec);
            }
        } else if (agent) {
            if (!state.agents[agent.id]) {
                dispatch(
                    receiveAgent({
                        id: agent.id,
                        avatar: agent.photo,
                        name: agent.name,
                    }),
                );
            }

            dispatch(setOperatorAvailable(state.history.operatorAvailable, true));
            dispatch(setLastAgent(agent.id, agent.type));
            let totalTime = Math.round((Date.now() - startTime) / 1000);

            if (totalTime > 0 && startTime !== 0 && expectedTime !== 0) {
                const percent = Math.round((totalTime / expectedTime) * 100);
                totalTime = totalTime - expectedTime;
                const diff = totalTime % 10;
                totalTime = totalTime - diff;
                totalTime = diff < 5 ? totalTime : totalTime + 10;

                services.analytics.send(
                    createReachGoalActionBy(AnalyticsTarget.AwaitTimeInSeconds, String(totalTime)),
                );
                services.analytics.send(
                    createReachGoalActionBy(AnalyticsTarget.AwaitTimeInPercent, String(percent)),
                );
            }

            startTime = 0;
            expectedTime = 0;

            dispatch(setOperatorAwaitTimeMs(null));
            localStorage.removeItem(StorageKey.TimeAlreadyAwaitedForOperatorMs);
            localStorage.removeItem(StorageKey.OperatorAwaitTimerCleanupTimestamp);
        } else {
            dispatch(setAppealClosedBy(state?.history?.lastAgent));
            dispatch(clearLastAgent());
        }
    },

    // обращение закрыто
    [WsAction.AppealClosed]: (action, dispatch, _token, getState) => {
        const state = getState();
        dispatch(setOperatorAwaitTimeMs(null));

        if (state.history.lastAgent) {
            dispatch(setOperatorAvailable(state.history.operatorAvailable, false));
            dispatch(setAppealClosedBy(state.history?.lastAgent));
            dispatch(clearLastAgent());
        }
    },

    // результаты обработки аудио-пакетов
    [WsAction.AsrResult]: (action, dispatch) => {
        try {
            const { success, data, signature, uuid, error } = action as WsAsrResultReplyMessage;

            if (!success) {
                if (error) {
                    const { type, text } = error;

                    switch (type) {
                        case 1:
                            services.analytics.sendAnalyticsAction(
                                'недоступность или ошибка в работе filestorage',
                            );
                            break;

                        case 2:
                            services.analytics.sendAnalyticsAction(
                                'недоступность или ошибка в работе chat_ASR_adapter',
                            );
                            break;

                        default:
                            break;
                    }

                    services.log.error(text);
                    services.widget.eventChannel.emit('toast', false, {
                        id: randomStr(),
                        message: text || clientSendVoiceError,
                        type: 'critical',
                    });
                    services.widget.eventChannel.emit('stopVoiceRecording', false);
                }

                dispatch(rollbackLastAsrResult());
                return;
            }

            dispatch(commitLastAsrResult({ uuid, data, signature }));
        } catch (e) {
            services.log.error(String(e));
        }
    },

    // получение сообщения
    [WsAction.ReceiveMessage]: (action, dispatch, _token, getState) => {
        const dataFields = [action.data];

        if (action.additional_data) {
            dataFields.push(...action.additional_data);
        }

        dataFields.forEach((wsMsg) => {
            if (wsMsg) {
                const msg = wsToChatMessage(wsMsg, true);

                if (isMessageCommand(msg)) {
                    if (msg.agentId !== clientAgentId) {
                        dispatch(receiveCommand(msg.metadata.command));
                    }
                } else if (sharingMessages[msg.type]) {
                    dispatch(receiveSharingMessage(msg.type));
                } else if (isMessageReadable(msg)) {
                    _counterLog.log(`WsAction.ReceiveMessage`, `msg.id => ${msg.id}`);
                    const list = getState().history.messagesReceivedOnlineIds;
                    dispatch(setMessagesReceivedOnlineIdList([...list, msg.id]));
                    dispatch(receiveMessage(msg));
                }
            }
        });
    },

    [WsAction.MessageReceived]: (action, dispatch) => {
        dispatch(markMessageReceived(action.data.id));
    },

    [WsAction.MessageRead]: (action, dispatch) => {
        dispatch(markMessageRead(action.data.id));
    },

    [WsAction.ReceiveAgent]: (action, dispatch) => {
        const agent = wsToChatAgent(action.data);

        if (agent.id !== techAgentId) {
            dispatch(receiveAgent(agent));
        }
    },

    [WsAction.Typing]: (_, dispatch) => {
        dispatch(receiveAgentTyping());
    },

    [WsAction.CsiRequest]: (_, dispatch, _token, getState) => {
        const { chat, history } = getState();

        dispatch(csiReset());
        dispatch(setAppealClosedBy(history?.lastAgent));
        dispatch(setOperatorAwaitTimeMs(null));

        dispatch(
            receiveInputWidget({
                elements: [
                    {
                        type: InputWidgetElementType.CSI,
                        elementId: randomUuid(),
                    },
                ],
                forceShowChatFooter: true,
            }),
        );

        dispatch(
            setCsiState({
                hasBeenRead: chat.open,
                wasReceived: true,
            }),
        );
    },

    [WsAction.UnreadCounterUpdate]: (action, dispatch) => {
        const counter = (action?.data?.unread_counter as number) ?? 0;
        if (counter) {
            _counterLog.log(`WsAction.UnreadCounterUpdate`, `counter => ${counter}`);
            dispatch(setUnreadCounter(counter));
        }
    },

    [WsAction.SharingStarted]: (action, dispatch, token) => {
        const roomId = action?.data?.room_id;
        if (roomId) {
            dispatch(startSharingViewer(token, roomId));
        }
    },

    [WsAction.SharingStopped]: (action, dispatch) => {
        dispatch(stopSharingViewer());
    },

    [WsAction.IN_OPERATOR_VIEWS_SHARE]: (action, dispatch) => {
        const messageType = MessageType.SharingRemoteViewStart;
        const msg: MessageData = {
            type: messageType,
            time: Math.floor(Date.now() / 1000),
            uuid: randomUuid(),
            text: sharingMessages[messageType],
            agentId: techAgentId,
        };

        if (isMessageReadable(msg)) {
            dispatch(addClientSideMessage(msg));
        }
    },
    [WsAction.REACTION_READ_REPLY]: (action, dispatch, _token, getState) => {
        const state = getState();
        const { unreadReactions } = state.history;
        const msgId = action.messageId ?? action.message_id;

        dispatch(
            setUnreadReactions(
                unreadReactions.filter((reaction) => String(reaction.message_id) != msgId),
            ),
        );
    },
    [WsAction.REACTION_ADDED]: (action: ReactionAddedMessage, dispatch, _token, getState) => {
        const state = getState();
        const { unreadReactions } = state.history;
        services.analytics.send(createReachGoalActionBy(AnalyticsTarget.OperatorReactions));
        dispatch(
            setUnreadReactions([
                ...unreadReactions.filter((e) => e.message_id !== action.data.message_id),
                action.data,
            ]),
        );
        dispatch(
            setMessageReactions(action.data.message_id, [
                {
                    id: action.data.reaction_id,
                    clientId: action.data.reaction_id,
                },
            ]),
        );
    },
    [WsAction.REACTION_REMOVED]: (action: ReactionRemovedMessage, dispatch, _token, getState) => {
        const state = getState();
        const { unreadReactions } = state.history;
        dispatch(setMessageReactions(action.data.message_id, []));
        dispatch(
            setUnreadReactions(
                unreadReactions.filter((e) => e.message_id !== action.data.message_id),
            ),
        );
    },
    [WsAction.UnreadCounterReply]: (action, dispatch) => {
        const counter = (action?.data?.unread_counter as number) ?? 0;
        if (counter) {
            _counterLog.log(`WsAction.UnreadCounterReply`, `counter => ${counter}`);
            dispatch(setUnreadCounter(counter));
        }
    },
};

export class IncomingActionServiceImpl implements IncomingActionService {
    private dispatch: Dispatch = null;
    private getState: () => DefaultRootState = null;

    public configure({ widgetStore }: ServiceConfiguration): void {
        this.dispatch = widgetStore.dispatch.bind(widgetStore);
        this.getState = widgetStore.getState.bind(widgetStore);
    }

    public handle = (action: BaseWsMessage, token?: string) => {
        const handler = incomingActionHandlers?.[action.action];

        try {
            if (this.dispatch && handler) {
                handler.call(this, action, this.dispatch, token, this.getState);
                return true;
            } else {
                return services.sharing.handle(action);
            }
        } catch (e) {
            services.log.error(
                `Failed to process incoming action (${action.action}): ${String(e)}`,
            );

            return false;
        }
    };
}
